// Self-occlusion among ONE object's own faces in a generated section's
// elevation (docs/claude/sections.md, *Sections*): a room's wall faces, an extruded polygon's
// sides. Each face is drawn only where no NEARER face of the same object hides
// it, and each point of what it shows is painted at the depth the object's STACK
// of faces there gives it (`paintAt`): a face turned away from the viewer never
// paints over the face standing in front of it, and the face in front still
// hides whatever the face behind it used to hide. Pure and DOM-free
// (test/section.test.mjs).
//
// Every region a face draws is t-monotone (a band between two profiles over a
// span of t), so the clip works column by column, exactly: no perturbation, no
// grid, every vertex where the geometry puts it.

import {
  bridgeHoles,
  convexMerge,
  earClip,
} from '../shared/triangulate';
import type {
  PolyStyle,
  SecDraw,
  SecPt,
  SecSrc,
  SectionPiece,
} from './section';

/** One step of paint depth: what is seen THROUGH a nearer face's holes is
 *  painted this far behind that face (before its glass, its window frames and
 *  its outline), and a face standing in front of a face whose tag is nearer
 *  than its own this far in front of that tag. */
export const SEEN_THROUGH = 1e-6;

/** Two columns closer than this are one. */
const EPS_T = 1e-9;
/** Two heights at one column closer than this are one point. */
const EPS_V = 1e-7;
/** A band thinner than this at a column's middle is none. */
const THIN = 1e-6;
/** A piece narrower than this (twice its area over its perimeter) is a sliver, and dropped. */
const SLIVER = 1e-3;

/** Which piece styles paint OPAQUE — hide what was painted before them —,
 *  `'alpha'` at α ≥ 0.99. Keyed by every style, so a new one does not compile
 *  until it is placed. */
const OPAQUE: Record<SectionPiece['style'], boolean | 'alpha'> = {
  profile: true, cut: true, 'cut-hatched': true, elev: true, roof: true, band: 'alpha',
  outline: false, window: false, glass: false, vedge: false, line: false,
};

/** Does this piece paint opaque — hiding what was painted before it? The ONE
 *  rule: the self-occlusion, the DXF export and the occlusion tests read it. */
export function paintsOpaque(p: Pick<SectionPiece, 'style' | 'alpha'>): boolean {
  const o = OPAQUE[p.style];
  return o === 'alpha' ? (p.alpha ?? 1) >= 0.99 : o;
}

/** The `SecDraw` members that draw (all but `src`). */
export type SecKind = Exclude<keyof SecDraw, 'src'>;

/** A recorded draw call: the `SecDraw` member, its arguments and the source it
 *  was drawn for — which every piece cut from it keeps. */
export interface SecCall { k: SecKind; a: unknown[]; src?: SecSrc }

/** One face of an object seen in elevation: its plan segment's two ends as (t
 *  along the cut, depth in front of it), the columns it is drawn over, its paint
 *  depth (its nearest point) and its calls, in order. */
export interface SecFace {
  ends: readonly [{ t: number; d: number }, { t: number; d: number }];
  s0: number;
  s1: number;
  d: number;
  calls: SecCall[];
  /** Columns of its holes no call paints in (a glazing's run, drawn apart):
   *  open as its glass and window frames' are. */
  open?: ReadonlyArray<readonly [number, number]>;
}

/** A call drawn, or a piece of one (a `poly`), at its paint depth. */
export interface SecShown { d: number; call: SecCall }

/** Is `f` in front of `w`? Over the columns both are drawn in (none, or a
 *  sliver: no), at their middle: two edges of a simple outline never cross, so
 *  the order found there holds over all of them. */
export function nearerFace(f: SecFace, w: SecFace): boolean {
  const o0 = Math.max(f.s0, w.s0), o1 = Math.min(f.s1, w.s1);
  if (o1 - o0 <= 1e-3) return false;
  const tm = (o0 + o1) / 2;
  return depthAt(f, tm) < depthAt(w, tm) - 1e-6;
}

function depthAt(f: SecFace, t: number): number {
  const [p, q] = f.ends;
  return q.t === p.t ? Math.min(p.d, q.d) : p.d + ((q.d - p.d) * (t - p.t)) / (q.t - p.t);
}

// ---- The paint depth at a point ------------------------------------------------------

/** A paint depth `b − k·SEEN_THROUGH`: a face's own tag `b`, moved `k` steps
 *  nearer (behind, for a negative `k`). Kept as the pair while it is worked
 *  out, so a step forward and back is exactly where it started. */
interface Depth { b: number; k: number }
const valueOf = (x: Depth): number => x.b - x.k * SEEN_THROUGH;
const cmpDepth = (x: Depth, y: Depth): number => (x.b === y.b ? y.k - x.k : valueOf(x) - valueOf(y));

/** A face at a point: its tag, and whether it paints opaque there. */
interface Layer { d: number; opaque: boolean }

/**
 * The paint depth of the face at index `w` of `stack`: the object's faces at a
 * point, NEAREST FIRST at its column — those in front of it whose silhouette
 * holds the point (it is seen through their holes), the face, and those behind
 * it whose silhouette holds it, each flagged where it paints opaque.
 *
 * Each face has a FIELD: its own tag — unless a face behind it is tagged nearer
 * than it by more than a step (an INVERSION: a face turned away whose nearest
 * point is the nearer); then one step in front of the nearest such tag, so the
 * face in front hides whatever that face's tag hid. A face painting OPAQUE
 * there reads only the faces behind it opaque there — where they leave a hole,
 * what stands in front of the room shows through it —; one that does not (its
 * glass, a window frame, its outline) reads them by their silhouettes, holes
 * included, so what that face's tag hid behind its pane stays under the glass
 * in front. The faces are painted nearest first, each at its field or one step
 * behind the face before it, whichever is the farther: what is seen through a
 * face's holes is painted under its glass, never over it.
 */
function paintAt(stack: readonly Layer[], w: number): Depth {
  // The least tags behind: of the faces opaque there, of all of them.
  let mo = Infinity, ms = Infinity;
  for (let k = stack.length - 1; k > w; k--) { const { d, opaque } = stack[k]; if (opaque && d < mo) mo = d; if (d < ms) ms = d; }
  const f: Depth[] = new Array(w + 1);
  for (let k = w; k >= 0; k--) {
    const { d, opaque } = stack[k], m = opaque ? mo : ms;
    f[k] = m < d - SEEN_THROUGH ? { b: m, k: 1 } : { b: d, k: 0 };
    if (opaque && d < mo) mo = d;
    if (d < ms) ms = d;
  }
  let p = f[0];
  for (let k = 1; k <= w; k++) {
    const behind = { b: p.b, k: p.k - 1 };
    p = cmpDepth(behind, f[k]) > 0 ? behind : f[k];
  }
  return p;
}

// ---- The faces ----------------------------------------------------------------------

/**
 * What is drawn of each face: every call minus the OPAQUE calls of each face in
 * front of it, each point of it at the paint depth its stack of faces gives it
 * (`paintAt`). A call nothing covers, painted at one depth, stays the call it
 * was; one the faces in front cut, a `poly` per piece. One painted at several
 * depths is, when OPAQUE, its whole visible region at the farthest of them and
 * a MASK over each part painted nearer: the same style and colour, every point
 * a sample (`s`), the edges along the call's own outline repeats (`r`), the
 * others clip edges (`x`) — it repaints its own colour and outline over what
 * stands between the two depths, and adds no vertex and no snap run. When not
 * opaque (glass, a window frame, the silhouette) it is split exactly where the
 * depth changes, the split edges clip edges and a split point on a straight
 * edge of its own a sample.
 *
 * `general`: no fast path — every call clipped whole, an extruded polygon's
 * sides as regions —, the test seam that pins the fast paths to it. `sweep`:
 * the faces worked out nearest first wherever that holds (true) or pair by
 * pair (false), not as the pairs of them overlapping decide (`manyPairs`) — the
 * seam that pins the one to the other. `work`: what that took, for the cost
 * tests.
 */
export function occludeFaces(faces: readonly SecFace[], { general = false, sweep, work }: { general?: boolean; sweep?: boolean; work?: OcclusionWork } = {}): SecShown[][] {
  const tally: OcclusionWork = work ?? { swept: false, pairs: 0, steps: 0 };
  tally.swept = false; tally.pairs = 0; tally.steps = 0;
  if (faces.length < 2) return faces.map(whole);
  if (!general && extruded(faces)) return occludeExtruded(faces);
  let order = sweep === undefined && faces.length > SWEPT_MIN ? byStart(faces) : null;
  const nearest = !general && (sweep ?? (!!order && manyPairs(faces, order, tally)));
  // Nearest first counts every face's calls and region it looks up; pair by
  // pair stays within the pairs it counts.
  const info: Array<FaceInfo | undefined> = new Array(faces.length);
  const infoOf = nearest
    ? (j: number): FaceInfo => { tally.steps++; return (info[j] ??= faceInfo(faces[j], tally)); }
    : (j: number): FaceInfo => (info[j] ??= faceInfo(faces[j], null));
  if (nearest) {
    const swept = occludeSwept(faces, infoOf, tally);
    if (swept) return swept;
  }
  if (!order && faces.length > SWEPT_MIN) order = byStart(faces);
  const { front, behind } = relations(faces, order, tally);
  // The INVERTED pairs: a face behind another, tagged nearer than it by more than a step.
  const inverted: Array<[number, number]> = [];
  faces.forEach((w, i) => { for (const j of front[i]) if (w.d < faces[j].d - SEEN_THROUGH) inverted.push([j, i]); });
  let inFront: Array<Set<number>> | null = null;
  const isFront = (i: number, j: number): boolean => (inFront ??= front.map((l) => new Set(l)))[i].has(j);
  return faces.map((w, i) => {
    const near = front[i];
    // With an inversion among the faces it overlaps, the fields vary: the faces
    // in front and those behind (by their silhouettes, flagged where they paint
    // opaque) set its depth. Without one, every field is its face's tag, and
    // only a face in front tagged within a few steps of it can push it back.
    let inv = false;
    if (inverted.length) { const zone = new Set([i, ...near, ...behind[i]]); inv = inverted.some(([a, b]) => { tally.steps++; return zone.has(a) && zone.has(b); }); }
    const pushers = inv || near.some((j) => faces[j].d + (near.length + 1) * SEEN_THROUGH > w.d) ? near : [];
    const below = inv ? behind[i] : [];
    if (!near.length && !below.length) return whole(w);
    const own = infoOf(i), out: SecShown[] = [], cover = coverOf(near, infoOf);
    const tagged = (): Depth => ({ b: w.d, k: 0 });
    w.calls.forEach((call, k) => {
      const S = own.region(k);
      if (!S) { out.push({ d: w.d, call }); return; }
      const occ = cover(S);
      const dec = own.dec[k]!, opaque = paintsOpaque(dec);
      // The zones that set its depth: the silhouettes of the faces in front;
      // where those behind paint opaque and, when a face painting no opaque
      // field reads them (its own, if this call is not opaque, or one in front),
      // their silhouettes; and, for a call that is not opaque, where its own
      // face paints opaque (its field is then the opaque one).
      const zones: Zone[] = [];
      for (const j of pushers) zonesHitting(zones, j, infoOf(j), infoOf(j).sil, S, false);
      const silBelow = !opaque || pushers.length > 0;
      for (const j of below) {
        const f = infoOf(j);
        zonesHitting(zones, j, f, f.opq, S, true);
        if (silBelow && f.sil !== f.opq) zonesHitting(zones, j, f, f.sil, S, false);
      }
      if (inv && !opaque) zonesHitting(zones, i, own, own.opq, S, true);
      if (!occ.length && !zones.length) { out.push({ d: w.d, call }); return; }
      // Only where no face in front stands over its whole height without a
      // hole can anything of it be seen: the rest is skipped, cells unbuilt.
      let windows: Array<[number, number]> = [[S.t0, S.t1]];
      if (occ.length && !general) {
        const solid: Array<[number, number]> = [];
        for (const j of near) solidColumns(S, infoOf(j), solid);
        windows = minus(windows, solid);
        if (!windows.length) return;
      }
      const depthOf = !zones.length ? tagged : stackDepth(faces, i, opaque, isFront);
      if (windows.length === 1 && windows[0][0] <= S.t0 && windows[0][1] >= S.t1) {
        const fast = !general && !zones.length ? bandsCut(S, occ) : null;
        if (fast) for (const r of fast) out.push({ d: w.d, call: polyCall(dec, r) });
        else { const G = cells(S, occ, zones, depthOf); for (const s of shownOf(call, dec, [gridPart(G)], G)) out.push(s); }
        return;
      }
      const parts: Part[] = [];
      for (let v = 0; v < windows.length; v++) {
        const Sw = restrict(S, windows[v][0], windows[v][1]);
        if (!Sw) continue;
        const fast = !zones.some((z) => hits(z.r, Sw)) ? bandsCut(Sw, cover(Sw)) : null;
        // (The whole call's occluders and zones: one touching the window's end
        // still makes the heights there one point, as without the window.)
        parts.push(fast ? bandsPart(fast, w.d) : gridPart(cells(Sw, occ, zones, depthOf)));
      }
      for (const s of shownOf(call, dec, parts, null)) out.push(s);
    });
    return out;
  });
}

const whole = (w: SecFace): SecShown[] => w.calls.map((call) => ({ d: w.d, call }));

/** The paint depth of a call of face `i` (`opaque`: whether it paints opaque)
 *  at a point of column `tm`, from the zones holding it — `at`: each as its
 *  face twice over plus 1 where it paints opaque —, `isFront(a, b)` whether
 *  face b stands in front of face a: its stack, nearest first, at `paintAt`. */
function stackDepth(faces: readonly SecFace[], i: number, opaque: boolean, isFront: (a: number, b: number) => boolean): (tm: number, at: readonly number[]) => Depth {
  const w = faces[i];
  /** Two faces at a column: the order found where they overlap, else the depth there. */
  const order = (tm: number) => (a: number, b: number): number =>
    (isFront(a, b) ? 1 : isFront(b, a) ? -1 : depthAt(faces[a], tm) - depthAt(faces[b], tm) || a - b);
  return (tm, at) => {
    const fr: number[] = [], bh: number[] = [], bo: boolean[] = [];
    let self = opaque;
    for (const x of at) {
      const j = x >> 1, o = (x & 1) === 1;
      if (j === i) { if (o) self = true; } else if (isFront(i, j)) { if (!fr.includes(j)) fr.push(j); } else {
        const k = bh.indexOf(j);
        if (k < 0) { bh.push(j); bo.push(o); } else if (o) bo[k] = true;
      }
    }
    if (!fr.length && !bh.length) return { b: w.d, k: 0 };
    const by = order(tm);
    fr.sort(by);
    const back = bh.map((j, k) => ({ j, o: bo[k] })).sort((a, b) => by(a.j, b.j));
    const layers: Layer[] = fr.map((j) => ({ d: faces[j].d, opaque: false }));
    layers.push({ d: w.d, opaque: self });
    for (const { j, o } of back) layers.push({ d: faces[j].d, opaque: o });
    return paintAt(layers, fr.length);
  };
}

/** Do two regions' boxes overlap by more than a hair? */
const hits = (r: Region, S: Region): boolean => r.t1 > S.t0 + EPS_T && r.t0 < S.t1 - EPS_T && r.h1 > S.h0 + THIN && r.h0 < S.h1 - THIN;

/** The regions of face `j`'s `calls` overlapping `S`, as its zones (`o`: where
 *  it paints opaque), added to `out`. */
function zonesHitting(out: Zone[], j: number, f: FaceInfo, calls: readonly number[], S: Region, o: boolean): void {
  for (const c of calls) { const r = f.region(c); if (r && hits(r, S)) out.push({ r, face: j, o }); }
}

/** The columns of `S` a face in front stands over to its whole height with no
 *  hole there, added to `out`: every silhouette of the face holding `S`'s
 *  height over a stretch of its columns, less the columns of its holes (its
 *  glass and window frames, which its walls leave open). `calls`, `u`, `v`: only
 *  those silhouettes, and only on the columns [u, v]. */
function solidColumns(S: Region, f: FaceInfo, out: Array<[number, number]>, calls: readonly number[] = f.sil, u = -Infinity, v = Infinity): void {
  for (const k of calls) {
    const R = f.region(k);
    if (!R) continue;
    const a = Math.max(R.t0, S.t0), b = Math.min(R.t1, S.t1);
    if (!(b - a > EPS_T) || !holds(R, S, a, b)) continue;
    const X = f.closed(k), x0 = Math.max(a, u), x1 = Math.min(b, v);
    let m = 0, hi = X.length;
    while (m < hi) { const c = (m + hi) >> 1; if (X[c][1] > x0) hi = c; else m = c + 1; }
    for (; m < X.length && X[m][0] < x1; m++) { const lo = Math.max(X[m][0], x0), up = Math.min(X[m][1], x1); if (up > lo) out.push([lo, up]); }
  }
}

/** Does `R` hold `S`'s whole height over [a, b]? Both are polylines in t, so it
 *  is enough to look at their vertices there and the two ends. */
function holds(R: Region, S: Region, a: number, b: number): boolean {
  if (R.rect && S.rect) return R.h0 <= S.h0 + EPS_V && R.h1 >= S.h1 - EPS_V;
  const ts = [a, b];
  for (const c of [R.lo, R.hi, S.lo, S.hi]) for (const t of c.t) if (t > a && t < b) ts.push(t);
  return ts.every((t) => valueAt(R.lo, t) <= valueAt(S.lo, t) + EPS_V && valueAt(R.hi, t) >= valueAt(S.hi, t) - EPS_V);
}

/** `S` over the columns [wa, wb] only: a window of it — its chains kept whole,
 *  so every height in it is the one the whole region has —, its sides there
 *  clip edges where they are not its own. */
function restrict(S: Region, wa: number, wb: number): Region | null {
  if (!(wb - wa > EPS_T)) return null;
  const l = wa > S.t0 + EPS_T, r = wb < S.t1 - EPS_T;
  const at = (x: number): [number, number] => [valueAt(S.lo, x), valueAt(S.hi, x)];
  const flags = (x: number): Array<boolean | undefined> => {
    const a = vertexAt(S.lo, x), b = vertexAt(S.hi, x);
    return [a >= 0 ? S.loS[a] : undefined, b >= 0 ? S.hiS[b] : undefined];
  };
  let h0 = Infinity, h1 = -Infinity;
  for (const x of [wa, wb]) { const [a, b] = at(x); h0 = Math.min(h0, a); h1 = Math.max(h1, b); }
  S.lo.t.forEach((x, i) => { if (x > wa && x < wb) h0 = Math.min(h0, S.lo.h[i]); });
  S.hi.t.forEach((x, i) => { if (x > wa && x < wb) h1 = Math.max(h1, S.hi.h[i]); });
  return {
    ...S, t0: wa, t1: wb, h0, h1,
    left: l ? at(wa) : S.left, leftS: l ? flags(wa) : S.leftS,
    right: r ? at(wb) : S.right, rightS: r ? flags(wb) : S.rightS,
    leftClip: l || S.leftClip, rightClip: r || S.rightClip,
  };
}

/** A rectangle `S` whose occluders all span its columns: its pieces are the
 *  stretches of its height they leave — what `cells` and `rings` make of it,
 *  without them —, or null when that does not hold. */
function bandsCut(S: Region, occ: readonly Region[]): SecPt[][] | null {
  if (!S.rect) return null;
  const ivs: Array<{ a: number; b: number }> = [];
  for (const o of occ) {
    if (!o.rect || !(o.t0 <= S.t0 + EPS_T && o.t1 >= S.t1 - EPS_T)) return null;
    const a = Math.max(o.h0, S.h0), b = Math.min(o.h1, S.h1);
    if (b - a > THIN) ivs.push({ a, b });
  }
  ivs.sort((x, y) => x.a - y.a);
  const out: SecPt[][] = [];
  const left = !S.leftClip, right = !S.rightClip;
  // Each corner a sample only between two edges neither of its own (a corner
  // of what covers it), a vertex where one of its own straight edges meets
  // another edge.
  const pt = (t: number, h: number, before: boolean, after: boolean): SecPt => {
    const p: SecPt = { t, h };
    if (!before && !after) p.s = true;
    if (!after) p.x = true;
    return p;
  };
  const piece = (lo: number, hi: number, ownBottom: boolean, ownTop: boolean): void => {
    const r = [pt(S.t0, lo, left, ownBottom), pt(S.t1, lo, ownBottom, right), pt(S.t1, hi, right, ownTop), pt(S.t0, hi, ownTop, left)];
    if (notSliver(r)) out.push(r);
  };
  let cur = S.h0, ownLo = true;
  for (const iv of ivs) {
    if (iv.a > cur + THIN) piece(cur, iv.a, ownLo, false);
    if (iv.b > cur) { cur = iv.b; ownLo = false; }
  }
  if (S.h1 > cur + THIN) piece(cur, S.h1, ownLo, true);
  return out;
}

/** Which faces stand in front of which: `front[i]` the faces in front of face
 *  i, `behind[i]` those behind it, each in face order (the order its occluders
 *  are traced in). Only faces whose columns overlap are compared: a room's few
 *  faces pair by pair, more in their `order` by where they start (`byStart`),
 *  each pair a step of `tally`. */
function relations(faces: readonly SecFace[], order: readonly number[] | null, tally: OcclusionWork): { front: number[][]; behind: number[][] } {
  const n = faces.length;
  const front: number[][] = [], behind: number[][] = [];
  for (let i = 0; i < n; i++) { front.push([]); behind.push([]); }
  // `nearerFace` both ways, at the middle of the overlap.
  const relate = (i: number, j: number, s0: number, s1: number): void => {
    tally.steps++;
    if (s1 - s0 <= 1e-3) return;
    const tm = (s0 + s1) / 2, di = depthAt(faces[i], tm), dj = depthAt(faces[j], tm);
    if (dj < di - 1e-6) { front[i].push(j); behind[j].push(i); } else if (di < dj - 1e-6) { front[j].push(i); behind[i].push(j); }
  };
  if (!order) {
    for (let i = 0; i < n; i++) for (let j = i + 1; j < n; j++) relate(i, j, Math.max(faces[i].s0, faces[j].s0), Math.min(faces[i].s1, faces[j].s1));
    return { front, behind };
  }
  for (let x = 0; x < n; x++) {
    const s1 = faces[order[x]].s1;
    for (let y = x + 1; y < n; y++) {
      const b0 = faces[order[y]].s0;
      if (b0 >= s1 - 1e-3) break; // no later face overlaps it
      relate(order[x], order[y], b0, Math.min(s1, faces[order[y]].s1));
    }
  }
  for (const l of front) l.sort((a, b) => a - b);
  for (const l of behind) l.sort((a, b) => a - b);
  return { front, behind };
}

/** A face's calls decoded, its regions (built when first asked for), the calls
 *  that paint opaque and those of its silhouette — its outlines, or, with none
 *  (an extruded polygon's side), its opaque calls. */
interface FaceInfo {
  dec: Array<Decoded | null>;
  region: (k: number) => Region | null;
  opq: number[];
  sil: number[];
  /** The columns of silhouette call `k` its holes (its glass and window
   *  frames) leave closed: sorted, apart. */
  closed: (k: number) => Array<[number, number]>;
}

/** With a `tally`, every region looked at is a step of it. */
function faceInfo(f: SecFace, tally: OcclusionWork | null): FaceInfo {
  const dec = f.calls.map(decode);
  const reg: Array<Region | null | undefined> = new Array(f.calls.length);
  const opq: number[] = [], outl: number[] = [], open: number[] = [];
  dec.forEach((d, k) => {
    if (!d) return;
    if (paintsOpaque(d)) opq.push(k);
    if (d.style === 'outline') outl.push(k);
    else if (d.style === 'glass' || d.style === 'window') open.push(k);
  });
  const built = (k: number): Region | null => (reg[k] === undefined ? (reg[k] = dec[k] ? regionOfDecoded(dec[k]!) : null) : reg[k]!);
  const region = tally ? (k: number): Region | null => { tally.steps++; return built(k); } : built;
  let cols: Array<[number, number]> | null = null;
  const holes = (): Array<[number, number]> => (cols ??= [...open.map((c): [number, number] => {
    const d = dec[c]!;
    if (d.quad) return [d.quad[0], d.quad[1]];
    const ts = d.ring!.map((p) => p.t);
    return [Math.min(...ts), Math.max(...ts)];
  }), ...(f.open ?? []).map(([a, b]): [number, number] => [a, b])]);
  const closed: Array<Array<[number, number]> | undefined> = [];
  return {
    dec, region, opq,
    sil: outl.length ? outl : opq,
    closed: (k) => (closed[k] ??= minus([[built(k)!.t0, built(k)!.t1]], holes())),
  };
}

/** The opaque regions of the faces in front of a face, found by their columns,
 *  each in the order they are traced in (face by face, call by call): with
 *  many, sorted by where they start under a tree of the farthest end, so a
 *  call finds its own in O(log n) and what it finds — the work stays
 *  proportional to what overlaps. */
function coverOf(near: readonly number[], infoOf: (j: number) => FaceInfo): (S: Region) => Region[] {
  const items: Region[] = [];
  for (const j of near) { const f = infoOf(j); for (const c of f.opq) { const r = f.region(c); if (r) items.push(r); } }
  if (items.length <= 16) return (S) => items.filter((r) => hits(r, S));
  const order = items.map((_, k) => k).sort((a, b) => items[a].t0 - items[b].t0);
  let size = 1;
  while (size < order.length) size *= 2;
  const far = new Float64Array(2 * size).fill(-Infinity);
  order.forEach((k, p) => { far[size + p] = items[k].t1; });
  for (let v = size - 1; v >= 1; v--) far[v] = Math.max(far[2 * v], far[2 * v + 1]);
  return (S) => {
    // Of those starting before S ends (the first n), the ones ending after it starts.
    const x0 = S.t0 + EPS_T, x1 = S.t1 - EPS_T;
    let n = 0, hi = order.length;
    while (n < hi) { const m = (n + hi) >> 1; if (items[order[m]].t0 < x1) n = m + 1; else hi = m; }
    const found: number[] = [];
    const visit = (v: number, l: number, r: number): void => {
      if (l >= n || !(far[v] > x0)) return;
      if (r - l === 1) { if (hits(items[order[l]], S)) found.push(order[l]); return; }
      const m = (l + r) >> 1;
      visit(2 * v, l, m); visit(2 * v + 1, m, r);
    };
    visit(1, 0, size);
    return found.sort((a, b) => a - b).map((k) => items[k]);
  };
}

// ---- Many faces: nearest first ---------------------------------------------------------

/** Up to this many faces, every pair of them is compared (`relations`), and
 *  they are worked out pair by pair unless asked otherwise (`sweep`). */
const SWEPT_MIN = 16;

/** Past this many pairs of faces overlapping in columns a face, they are
 *  worked out nearest first: pair by pair works each face through every face
 *  overlapping it, nearest first through what was painted last over it. */
const SWEPT_PAIRS = 4;

/** What `occludeFaces` did: whether it resolved the faces nearest first
 *  (`occludeSwept`); the pairs of faces overlapping in columns — what pair by
 *  pair compares —, counted until they decided that (`manyPairs`); and the
 *  steps it took, each counted where it is touched — every pair of faces
 *  compared or asked about and, nearest first, every face's calls and every
 *  region looked up, every run met, start of a run taken out and node of an
 *  index visited. */
export interface OcclusionWork { swept: boolean; pairs: number; steps: number }

/** The faces in the order of where they start. */
const byStart = (faces: readonly SecFace[]): number[] => faces.map((_, i) => i).sort((a, b) => faces[a].s0 - faces[b].s0);

/** Do more than `SWEPT_PAIRS` pairs a face overlap in columns — the pairs
 *  `relations` compares, each face (in its `order` by start) with those
 *  starting before it ends —, counted (into `tally`) only until they do? */
function manyPairs(faces: readonly SecFace[], order: readonly number[], tally: OcclusionWork): boolean {
  const n = order.length, cap = SWEPT_PAIRS * n, starts = new Float64Array(n);
  for (let x = 0; x < n; x++) starts[x] = faces[order[x]].s0;
  let pairs = 0;
  for (let x = 0; x < n && pairs <= cap; x++) {
    const end = faces[order[x]].s1 - 1e-3;
    let lo = x + 1, hi = n;
    while (lo < hi) { const m = (lo + hi) >> 1; if (starts[m] >= end) hi = m; else lo = m + 1; }
    pairs += lo - x - 1;
  }
  tally.pairs = pairs;
  return pairs > cap;
}

/** Is face j in front of face i, as `relations` finds: over the columns both
 *  are drawn in (`nearerFace`), the later to start (in that sort) starting more
 *  than 1e-3 short of the earlier one's end? */
function frontOf(faces: readonly SecFace[], i: number, j: number): boolean {
  const iFirst = faces[i].s0 < faces[j].s0 || (faces[i].s0 === faces[j].s0 && i < j);
  const a = faces[iFirst ? i : j], b = faces[iFirst ? j : i];
  return b.s0 < a.s1 - 1e-3 && nearerFace(faces[j], faces[i]);
}

/**
 * Columns painted over, each holding what was painted over it last: the cells
 * between the `ends` given (sorted, each once), the cells where a run starts —
 * and the last cell's end — a `CellSet`, each holding its run's painter in `by`
 * (-1: none), so a paint takes out the starts it covers and a run is met in a
 * step (of `tally`, as each start taken out). `paint(x0, x1, p)` paints [x0,
 * x1) — both ends given — with p ≥ 0. The runs sharing more than a point with
 * [x0, x1] start at the cells c from `first(x0, b)`, each ending at `next(c)`,
 * while c < b = `last(x1)`; cell c starts at column `X[c]`.
 */
class Painted {
  readonly X: Float64Array;
  readonly by: Int32Array;
  private readonly size: number;
  private readonly m: number;
  private readonly starts: CellSet;
  constructor(ends: readonly number[], private readonly tally: OcclusionWork) {
    const X = new Float64Array(ends).sort();
    let size = 0;
    for (let k = 0; k < X.length; k++) if (!size || X[k] > X[size - 1]) X[size++] = X[k];
    this.X = X; this.size = size; this.m = Math.max(0, size - 1);
    this.starts = new CellSet(this.m + 1);
    this.by = new Int32Array(this.m + 1).fill(-1);
    this.starts.add(0); this.starts.add(this.m);
  }
  /** The first of X at or past x (`past`: past it). */
  private at(x: number, past: boolean): number {
    const X = this.X;
    let lo = 0, hi = this.size;
    if (past) while (lo < hi) { const c = (lo + hi) >> 1; if (X[c] <= x) lo = c + 1; else hi = c; }
    else while (lo < hi) { const c = (lo + hi) >> 1; if (X[c] < x) lo = c + 1; else hi = c; }
    return lo;
  }
  paint(x0: number, x1: number, p: number): void {
    if (!(x1 > x0)) return;
    const S = this.starts, a = this.at(x0, false), b = this.at(x1, false), r = S.prev(b);
    if (r !== b) { S.add(b); this.by[b] = this.by[r]; }
    for (let c = S.next(a + 1); c < b; c = S.next(c + 1)) { this.tally.steps++; S.remove(c); }
    S.add(a); this.by[a] = p;
  }
  last(x1: number): number { return Math.min(this.at(x1, false), this.m); }
  first(x0: number, b: number): number {
    const a = Math.max(0, this.at(x0, true) - 1);
    return a < b ? this.starts.prev(a) : b;
  }
  next(c: number): number { this.tally.steps++; return this.starts.next(c + 1); }
}

/** A set of the integers [0, m) under a tree of 32-bit words, each bit of a
 *  word above saying whether a word below holds any: `next(x)` the least at or
 *  past x, `prev(x)` the greatest at or before it, each -1 for none. */
class CellSet {
  private readonly L: Int32Array[] = [];
  constructor(m: number) {
    for (let k = m; ; k = Math.ceil(k / 32)) { this.L.push(new Int32Array(Math.max(1, Math.ceil(k / 32)))); if (k <= 32) break; }
  }
  add(x: number): void { const L = this.L; for (let l = 0; l < L.length; l++, x >>= 5) { const had = L[l][x >> 5]; L[l][x >> 5] = had | (1 << (x & 31)); if (had) return; } }
  remove(x: number): void { const L = this.L; for (let l = 0; l < L.length; l++, x >>= 5) if ((L[l][x >> 5] &= ~(1 << (x & 31)))) return; }
  next(x: number): number {
    const L = this.L;
    let l = 0;
    for (;; l++, x = (x >> 5) + 1) {
      if (l === L.length || x >> 5 >= L[l].length) return -1;
      const bits = L[l][x >> 5] & (-1 << (x & 31));
      if (bits) { x = (x & ~31) | (31 - Math.clz32(bits & -bits)); break; }
    }
    for (; l > 0; l--) { const v = L[l - 1][x]; x = (x << 5) | (31 - Math.clz32(v & -v)); }
    return x;
  }
  prev(x: number): number {
    const L = this.L;
    let l = 0;
    for (;; l++, x = (x >> 5) - 1) {
      if (l === L.length || x < 0) return -1;
      const bits = L[l][x >> 5] & ~(-2 << (x & 31));
      if (bits) { x = (x & ~31) | (31 - Math.clz32(bits)); break; }
    }
    for (; l > 0; l--) x = (x << 5) | (31 - Math.clz32(L[l - 1][x]));
    return x;
  }
}

/** Intervals found by their columns once switched on (`set`): `find(x0, x1, f)`
 *  meets those on starting before x1 and ending after x0 until `f` says to stop,
 *  and says whether it did. */
interface ColumnIndex { set: (k: number, on: boolean) => void; find: (x0: number, x1: number, f: (k: number) => boolean) => boolean }

/** A `ColumnIndex` of the intervals [t0, t1]: sorted by where they start under
 *  a tree of the farthest end of those on (`coverOf`'s), each node met a step
 *  of `tally`. */
function intervalIndex(t0: readonly number[], t1: readonly number[], tally: OcclusionWork): ColumnIndex {
  const n = t0.length, order = Array.from({ length: n }, (_, k) => k).sort((a, b) => t0[a] - t0[b]);
  const at = new Int32Array(n), starts = new Float64Array(n);
  order.forEach((k, p) => { at[k] = p; starts[p] = t0[k]; });
  let size = 1;
  while (size < n) size *= 2;
  const far = new Float64Array(2 * size).fill(-Infinity);
  return {
    set: (k, on) => { let v = size + at[k]; far[v] = on ? t1[k] : -Infinity; for (v >>= 1; v >= 1; v >>= 1) far[v] = Math.max(far[2 * v], far[2 * v + 1]); },
    find: (x0, x1, f) => {
      let m = 0, hi = n;
      while (m < hi) { const c = (m + hi) >> 1; if (starts[c] < x1) m = c + 1; else hi = c; }
      const visit = (v: number, l: number, r: number): boolean => {
        if (l >= m || !(far[v] > x0)) return false;
        tally.steps++;
        if (r - l === 1) return f(order[l]);
        const h = (l + r) >> 1;
        return visit(2 * v, l, h) || visit(2 * v + 1, h, r);
      };
      return visit(1, 0, size);
    },
  };
}

/** A call under the faces in front (`occludeSwept`): HIT, one of those standing
 *  solid over a column of it hits it with an opaque call; HIDDEN, and those
 *  stand solid over all of it. */
const HIT = 1, HIDDEN = 2;

/** Going back from a face in the order taken, this many faces are asked
 *  whether they stand in front of it before those over its columns are. */
const BACK = 4;

/** A column bound past `x` toward `dir` by more than EPS_T and a rounding step
 *  of it: a region ending before it (starting after it) is no chain of any
 *  column of a window ending at `x` (`columns`, `canonical`, `cells`). */
function past(x: number, dir: 1 | -1): number {
  const y = x + dir * EPS_T;
  return y + dir * (EPS_T + Math.abs(y) * 4e-16);
}

/**
 * `occludeFaces` for many faces, worked out NEAREST FIRST — or null where that
 * order is not shown to hold, found before any face is worked out (in O(n log
 * n)), and the faces are compared pair by pair. The faces are taken by their
 * tags (of two tagged alike, the shallower first), and each must stand behind,
 * at every column of it, the face taken last there — the farthest one taken
 * there, by induction — or meet it at a corner they share (a margin over the
 * rounding of their depths): then no face stands in front of one taken before
 * it, so the faces in front of a face are all taken before it, none tagged
 * farther than it — no inversion.
 *
 * Each face is then worked out as pair by pair, from what has been taken: the
 * opaque calls and silhouettes of the faces in front found by their columns;
 * the columns a face in front stands over to a call's whole height with no
 * hole from the silhouettes painted SOLID over each column (each face's, less
 * its holes, over what was painted there before): a run whose last painter is
 * a face in front holding the call's height is solid, on any other the
 * silhouettes over it are read one by one. A call the faces in front hide is
 * settled by the few runs over it; one that shows reads only the regions about
 * its windows — those away from a window are no chain of any of its columns.
 */
function occludeSwept(faces: readonly SecFace[], infoOf: (j: number) => FaceInfo, tally: OcclusionWork): SecShown[][] | null {
  const n = faces.length, out: SecShown[][] = new Array(n), ids: number[] = [];
  let D = 0;
  for (let i = 0; i < n; i++) {
    const f = faces[i], p = f.ends[0], q = f.ends[1];
    if (!(Number.isFinite(f.s0) && Number.isFinite(f.s1) && Number.isFinite(f.d) && Number.isFinite(p.t) && Number.isFinite(p.d)
      && Number.isFinite(q.t) && Number.isFinite(q.d))) return null;
    D = Math.max(D, Math.abs(p.d), Math.abs(q.d));
    if (!(f.s1 - f.s0 > 1e-3)) { out[i] = whole(f); continue; } // in front of none, behind none
    if (f.s0 < Math.min(p.t, q.t) || f.s1 > Math.max(p.t, q.t)) return null; // drawn past its own ends
    ids.push(i);
  }
  const slope = new Float64Array(n);
  for (const i of ids) { const [p, q] = faces[i].ends; slope[i] = Math.abs((q.d - p.d) / (q.t - p.t)); }
  ids.sort((a, b) => faces[a].d - faces[b].d || slope[a] - slope[b] || a - b);
  // Two depths at a column are each off by at most ~1.3e-15·D.
  const margin = 1e-9 + 1e-14 * D;
  const endAt = (f: SecFace, x: number): number => (f.ends[0].t === x ? f.ends[0].d : f.ends[1].t === x ? f.ends[1].d : NaN);
  const behindAt = (i: number, c: number, x: number): boolean => {
    const e = endAt(faces[i], x);
    return (e === endAt(faces[c], x) && e === e) || depthAt(faces[i], x) - depthAt(faces[c], x) >= margin;
  };
  // The face taken last over each column; `alone`: a face no face taken before it overlaps.
  const starts = faces.map((f) => f.s0), ends = faces.map((f) => f.s1), spans: number[] = [];
  for (const i of ids) spans.push(starts[i], ends[i]);
  const F = new Painted(spans, tally), alone = new Uint8Array(n);
  for (const i of ids) {
    const s0 = starts[i], s1 = ends[i], b = F.last(s1);
    let met = false;
    for (let c = F.first(s0, b); c < b;) {
      const e = F.next(c), top = F.by[c];
      if (top >= 0) {
        met = true;
        if (!behindAt(i, top, Math.max(F.X[c], s0)) || !behindAt(i, top, Math.min(F.X[e], s1))) return null;
      }
      c = e;
    }
    if (!met) alone[i] = 1;
    F.paint(s0, s1, i);
  }
  const isFront = (a: number, b: number): boolean => frontOf(faces, a, b);
  const relFor = new Int32Array(n).fill(-1), relOf = new Uint8Array(n);
  /** Is face j in front of face i? Asked for one face i at a time. */
  const ahead = (i: number, j: number): boolean => {
    tally.steps++;
    if (relFor[j] !== i) { relFor[j] = i; relOf[j] = frontOf(faces, i, j) ? 1 : 0; }
    return relOf[j] === 1;
  };
  let took = 0; // the faces taken: ids[0 .. took)
  /** Each face's calls `of` it, found by their columns once their face is taken
   *  — the index built when first asked for. */
  const indexOf = (of: (f: FaceInfo) => readonly number[]) => {
    const J: number[] = [], C: number[] = [], lo = new Int32Array(n), hi = new Int32Array(n);
    let ix: ColumnIndex | null = null;
    const index = (): ColumnIndex => {
      if (ix) return ix;
      const t0: number[] = [], t1: number[] = [];
      for (const j of ids) {
        const f = infoOf(j);
        lo[j] = J.length;
        for (const c of of(f)) { const x = callSpan(f.dec[c]); if (x) { J.push(j); C.push(c); t0.push(x[0]); t1.push(x[1]); } }
        hi[j] = J.length;
      }
      ix = intervalIndex(t0, t1, tally);
      for (let q = 0; q < took; q++) for (let k = lo[ids[q]]; k < hi[ids[q]]; k++) ix.set(k, true);
      return ix;
    };
    const region = (k: number): Region | null => infoOf(J[k]).region(C[k]);
    return {
      J, C, region,
      take: (j: number): void => { if (ix) for (let k = lo[j]; k < hi[j]; k++) ix.set(k, true); },
      find: (x0: number, x1: number, f: (k: number) => boolean): boolean => index().find(x0, x1, f),
      /** Does a face in front of face i have one hitting S? */
      any: (i: number, S: Region): boolean => index().find(S.t0, S.t1, (k) => { const r = region(k); return !!r && hits(r, S) && ahead(i, J[k]); }),
      /** Those of the faces in front of face i hitting S on the columns (x0, x1), in face and call order. */
      hitting: (i: number, S: Region, x0: number, x1: number): number[] => {
        const found: number[] = [];
        index().find(x0, x1, (k) => { const r = region(k); if (r && hits(r, S) && ahead(i, J[k])) found.push(k); return false; });
        return found.sort((a, b) => J[a] - J[b] || C[a] - C[b]);
      },
    };
  };
  const opq = indexOf((f) => f.opq), sil = indexOf((f) => f.sil);
  // The silhouette painted solid last over each column, as its face and its
  // call, two by two in `painters`.
  const solidEnds: number[] = [];
  for (const i of ids) { const f = infoOf(i); for (const k of f.sil) if (f.region(k)) for (const [x0, x1] of f.closed(k)) solidEnds.push(x0, x1); }
  const W = new Painted(solidEnds, tally), painters: number[] = [];
  /** The columns of `S` a face in front of face i stands over to its whole
   *  height with no hole (`solidColumns`), added to `solid`; HIT when a face
   *  painted last over one of them hits S with an opaque call, HIDDEN when
   *  besides those faces so stand over all of S. */
  const solidOf = (i: number, S: Region, solid: Array<[number, number]>): number => {
    let hit = false, all = true, end = S.t0;
    const last = W.last(S.t1);
    for (let c = W.first(S.t0, last); c < last;) {
      const e = W.next(c), id = W.by[c];
      if (id >= 0) {
        const u = Math.max(W.X[c], S.t0), v = Math.min(W.X[e], S.t1);
        const j = painters[2 * id], f = infoOf(j), R = f.region(painters[2 * id + 1])!;
        const a = Math.max(R.t0, S.t0), b = Math.min(R.t1, S.t1);
        if (ahead(i, j) && b - a > EPS_T && holds(R, S, a, b)) {
          solid.push([u, v]);
          if (u !== end) all = false;
          end = v;
          if (!hit) for (const k of f.opq) { const r = f.region(k); if (r && hits(r, S)) { hit = true; break; } }
        } else {
          all = false;
          sil.find(u, v, (m) => { if (ahead(i, sil.J[m])) solidColumns(S, infoOf(sil.J[m]), solid, [sil.C[m]], u, v); return false; });
        }
      }
      c = e;
    }
    return !hit ? 0 : all && end === S.t1 ? HIDDEN : HIT;
  };
  // The faces taken tagged within n + 1 steps of the face resolved: ids[lo ..
  // took), found by their columns once looked for so (`within`); all those
  // taken, so (`taken`).
  let lo = 0, within = null as ColumnIndex | null, taken = null as ColumnIndex | null;
  /** Is the face at `p` pushed back by a face in front (`occludeFaces`' pushers):
   *  one tagged within as many steps of it as it has faces in front, and one? */
  const pushedBack = (p: number): boolean => {
    const i = ids[p], d = faces[i].d, reach = (n + 1) * SEEN_THROUGH;
    for (; faces[ids[lo]].d + reach <= d; lo++) { tally.steps++; within?.set(ids[lo], false); }
    // The nearest tag of those in front tagged within n + 1 steps: the first
    // in front going back — past a few, the nearest of those over its columns.
    let m = -Infinity, q = p - 1;
    for (const stop = Math.max(lo, p - BACK); q >= stop; q--) if (ahead(i, ids[q])) { m = faces[ids[q]].d; break; }
    if (m === -Infinity && q >= lo) {
      if (!within) { within = intervalIndex(starts, ends, tally); for (let r = lo; r < p; r++) within.set(ids[r], true); }
      within.find(starts[i], ends[i], (j) => { if (faces[j].d > m && ahead(i, j)) m = faces[j].d; return false; });
    }
    if (m === -Infinity) return false;
    if (m + 2 * SEEN_THROUGH > d) return true;
    // Else as many faces in front as it takes.
    if (!taken) { taken = intervalIndex(starts, ends, tally); for (let r = 0; r < p; r++) taken.set(ids[r], true); }
    let near = 0;
    return taken.find(starts[i], ends[i], (j) => ahead(i, j) && m + (++near + 1) * SEEN_THROUGH > d);
  };
  const resolve = (p: number): SecShown[] => {
    const i = ids[p], w = faces[i], own = infoOf(i), shown: SecShown[] = [], pushed = pushedBack(p);
    const tagged = (): Depth => ({ b: w.d, k: 0 });
    const occOf = (S: Region, x0: number, x1: number): Region[] => opq.hitting(i, S, x0, x1).map((k) => opq.region(k)!);
    const zonesOf = (S: Region, x0: number, x1: number): Zone[] =>
      (pushed ? sil.hitting(i, S, x0, x1).map((k) => ({ r: sil.region(k)!, face: sil.J[k], o: false })) : []);
    for (let k = 0; k < w.calls.length; k++) {
      const call = w.calls[k], S = own.region(k);
      if (!S) { shown.push({ d: w.d, call }); continue; }
      const solid: Array<[number, number]> = [], under = solidOf(i, S, solid);
      if (under === HIDDEN) continue;
      const covered = under === HIT || opq.any(i, S);
      let windows: Array<[number, number]> = [[S.t0, S.t1]];
      if (covered) { windows = minus(windows, solid); if (!windows.length) continue; }
      const zoned = pushed && sil.any(i, S), dec = own.dec[k]!;
      if (!covered && !zoned) { shown.push({ d: w.d, call }); continue; }
      const depthOf = zoned ? stackDepth(faces, i, paintsOpaque(dec), isFront) : tagged;
      if (windows.length === 1 && windows[0][0] <= S.t0 && windows[0][1] >= S.t1) {
        const occ = occOf(S, S.t0, S.t1), zones = zonesOf(S, S.t0, S.t1);
        const fast = !zones.length ? bandsCut(S, occ) : null;
        if (fast) for (const r of fast) shown.push({ d: w.d, call: polyCall(dec, r) });
        else { const G = cells(S, occ, zones, depthOf); for (const s of shownOf(call, dec, [gridPart(G)], G)) shown.push(s); }
        continue;
      }
      const parts: Part[] = [];
      for (const [wa, wb] of windows) {
        const Sw = restrict(S, wa, wb);
        if (!Sw) continue;
        const x0 = past(wa, -1), x1 = past(wb, 1), occ = occOf(S, x0, x1), zones = zonesOf(S, x0, x1);
        const fast = !zones.some((z) => hits(z.r, Sw)) ? bandsCut(Sw, occ.filter((r) => hits(r, Sw))) : null;
        parts.push(fast ? bandsPart(fast, w.d) : gridPart(cells(Sw, occ, zones, depthOf)));
      }
      for (const s of shownOf(call, dec, parts, null)) shown.push(s);
    }
    return shown;
  };
  for (let p = 0; p < ids.length; p++) {
    const i = ids[p], f = infoOf(i);
    out[i] = alone[i] ? whole(faces[i]) : resolve(p);
    took = p + 1;
    taken?.set(i, true); within?.set(i, true); opq.take(i); sil.take(i);
    for (const k of f.sil) {
      if (!f.region(k)) continue;
      painters.push(i, k);
      for (const [x0, x1] of f.closed(k)) W.paint(x0, x1, painters.length / 2 - 1);
    }
  }
  tally.swept = true;
  return out;
}

// ---- An extruded polygon: its sides on the columns ------------------------------------

/** Every face one opaque `elev` over one height band: an extruded polygon's sides. */
function extruded(faces: readonly SecFace[]): boolean {
  const c0 = faces[0].calls[0];
  if (!c0) return false;
  return faces.every((f) => f.calls.length === 1 && f.calls[0].k === 'elev' && f.calls[0].a[2] === c0.a[2] && f.calls[0].a[3] === c0.a[3]);
}

/**
 * An extruded polygon's sides share one height band and have no holes, so a
 * side is seen exactly over the columns where it is the NEAREST side, painted
 * there at its field over the sides behind it (nothing in front of it is seen
 * through) — `occludeFaces`' pieces, worked out on the columns in O(n log n):
 * the nearest side of each column by merging lower envelopes, the two least
 * tags over it by painting the sides in the order of their tags. It parts from
 * the general clip only by slivers under 1e-3 mm, where two sides overlap that
 * little (`relations` leaves them unordered), and on an outline crossing
 * itself, where it takes the side nearest in each column.
 */
function occludeExtruded(faces: readonly SecFace[]): SecShown[][] {
  const n = faces.length;
  // The columns: every side's ends, two closer than EPS_T one.
  const ends = new Float64Array(2 * n);
  faces.forEach((f, i) => { ends[2 * i] = f.s0; ends[2 * i + 1] = f.s1; });
  ends.sort();
  const T: number[] = [];
  for (const t of ends) if (!T.length || t - T[T.length - 1] > EPS_T) T.push(t);
  const col = (t: number): number => {
    let lo = 0, hi = T.length - 1;
    while (lo < hi) { const m = (lo + hi + 1) >> 1; if (T[m] <= t + EPS_T) lo = m; else hi = m - 1; }
    return lo;
  };
  const C0 = new Int32Array(n), C1 = new Int32Array(n);
  faces.forEach((f, i) => { C0[i] = col(f.s0); C1[i] = col(f.s1); });
  // The nearest side over each run of columns: runs as (first column, end
  // column, side) triples, the envelopes of halves of the sides merged.
  const merge = (A: readonly number[], B: readonly number[]): number[] => {
    const out: number[] = [];
    const put = (c0: number, c1: number, i: number): void => {
      if (c1 <= c0) return;
      const l = out.length - 3;
      if (l >= 0 && out[l + 2] === i && out[l + 1] === c0) out[l + 1] = c1; else out.push(c0, c1, i);
    };
    let a = 0, b = 0, u = -1; // u: the first column not yet put
    while (a < A.length || b < B.length) {
      const p0 = a < A.length ? Math.max(A[a], u) : Infinity, q0 = b < B.length ? Math.max(B[b], u) : Infinity;
      if (a < A.length && (b >= B.length || A[a + 1] <= q0)) { put(p0, A[a + 1], A[a + 2]); u = A[a + 1]; a += 3; continue; }
      if (b < B.length && (a >= A.length || B[b + 1] <= p0)) { put(q0, B[b + 1], B[b + 2]); u = B[b + 1]; b += 3; continue; }
      const x = Math.max(p0, q0), y = Math.min(A[a + 1], B[b + 1]), i = A[a + 2], j = B[b + 2];
      if (p0 < x) put(p0, x, i); else if (q0 < x) put(q0, x, j);
      const tm = (T[x] + T[y]) / 2, di = depthAt(faces[i], tm), dj = depthAt(faces[j], tm);
      put(x, y, di < dj || (di === dj && i < j) ? i : j);
      u = y;
      if (A[a + 1] === y) a += 3;
      if (B[b + 1] === y) b += 3;
    }
    return out;
  };
  const envelope = (lo: number, hi: number): number[] => {
    if (hi - lo === 1) return [C0[lo], C1[lo], lo];
    const m = (lo + hi) >> 1;
    return merge(envelope(lo, m), envelope(m, hi));
  };
  // The two sides of least tag over each column: the sides painted in the
  // order of their tags, each column once in each layer.
  const cells = T.length - 1;
  const least = [new Int32Array(cells).fill(-1), new Int32Array(cells).fill(-1)];
  const next = [new Int32Array(cells + 1), new Int32Array(cells + 1)];
  for (let k = 0; k <= cells; k++) next[0][k] = next[1][k] = k;
  const free = (nx: Int32Array, k: number): number => {
    let r = k;
    while (nx[r] !== r) r = nx[r];
    while (nx[k] !== r) { const up = nx[k]; nx[k] = r; k = up; }
    return r;
  };
  const tags = new Float64Array(n), byTag = new Int32Array(n);
  faces.forEach((f, i) => { tags[i] = f.d; byTag[i] = i; });
  for (const i of byTag.sort((a, b) => tags[a] - tags[b])) {
    for (let k = free(next[0], C0[i]); k < C1[i]; k = free(next[0], k + 1)) { least[0][k] = i; next[0][k] = k + 1; }
    for (let k = free(next[1], C0[i]); k < C1[i]; k = free(next[1], k + 1)) if (least[0][k] !== i) { least[1][k] = i; next[1][k] = k + 1; }
  }
  // Each side's runs of columns where it is the nearest, at its field there:
  // (first column, end column, depth) triples.
  const runs: number[][] = [];
  const env = envelope(0, n);
  for (let e = 0; e < env.length; e += 3) {
    const i = env[e + 2], own = tags[i], list = runs[i] ?? (runs[i] = []);
    for (let k = env[e]; k < env[e + 1]; k++) {
      const o = least[0][k] === i ? least[1][k] : least[0][k];
      const m = o < 0 ? Infinity : tags[o];
      const d = m < own - SEEN_THROUGH ? m - SEEN_THROUGH : own;
      const l = list.length - 3;
      if (l >= 0 && list[l + 2] === d && list[l + 1] === k) list[l + 1] = k + 1; else list.push(k, k + 1, d);
    }
  }
  return faces.map((w, i) => {
    const list = runs[i];
    return list ? extrudedShown(w.calls[0], list, C0[i], C1[i], T) : [];
  });
}

/** What one side of an extruded polygon shows of its runs (as `shownOf`): the
 *  call, or its pieces, at the farthest of its depths, and a mask over each
 *  run painted nearer. */
function extrudedShown(call: SecCall, list: readonly number[], c0: number, c1: number, T: readonly number[]): SecShown[] {
  const [t0, t1, h0, h1, color] = call.a as [number, number, number, number, string];
  const at = (c: number): number => (c === c0 ? t0 : c === c1 ? t1 : T[c]); // its own ends its own
  const kept = (a: number, b: number): boolean => { const wd = at(b) - at(a), H = h1 - h0; return wd > EPS_T && wd * H > 1e-6 && (wd * H) / (wd + H) >= SLIVER; };
  const rect = (a: number, b: number): SecPt[] => [
    { t: at(a), h: h0 }, { t: at(b), h: h0, ...(b < c1 ? { x: true } : {}) }, { t: at(b), h: h1 }, { t: at(a), h: h1, ...(a > c0 ? { x: true } : {}) },
  ];
  const elev = (ring: SecPt[]): SecCall => (call.src ? { k: 'poly', a: [ring, 'elev', color], src: call.src } : { k: 'poly', a: [ring, 'elev', color] });
  const vis: number[] = [], depths: number[] = [];
  for (let r = 0; r < list.length; r += 3) {
    if (vis.length && vis[vis.length - 1] === list[r]) vis[vis.length - 1] = list[r + 1]; else vis.push(list[r], list[r + 1]);
    if (!depths.includes(list[r + 2])) depths.push(list[r + 2]);
  }
  const far = Math.max(...depths);
  const out: SecShown[] = [];
  if (vis.length === 2 && vis[0] === c0 && vis[1] === c1) out.push({ d: far, call });
  else for (let v = 0; v < vis.length; v += 2) if (kept(vis[v], vis[v + 1])) out.push({ d: far, call: elev(rect(vis[v], vis[v + 1])) });
  for (const d of depths) if (d !== far) for (let r = 0; r < list.length; r += 3) if (list[r + 2] === d && kept(list[r], list[r + 1])) out.push({ d, call: elev(maskOf(rect(list[r], list[r + 1]))) });
  return out;
}

/** A MASK's ring from a piece's: every point a sample, every edge that is no
 *  clip edge a repeat of its call's own. */
function maskOf(r: readonly SecPt[]): SecPt[] {
  return r.map((p) => ({ t: p.t, h: p.h, s: true, ...(p.c ? { c: true } : {}), ...(p.x ? { x: true } : { r: true }) }));
}

// ---- Calls as regions ------------------------------------------------------------

interface Decoded { quad: readonly [number, number, number, number] | null; ring: readonly SecPt[] | null; style: PolyStyle; color: string; alpha?: number; src?: SecSrc }

/** A call's region and how it paints — what it would be as a `poly` — or null
 *  for a line, which covers nothing and is never clipped. Every `SecDraw`
 *  member is placed here, so a new one does not compile until it is. */
function decode(call: SecCall): Decoded | null {
  const dec = decodeLook(call);
  return dec && call.src ? { ...dec, src: call.src } : dec;
}

function decodeLook(call: SecCall): Decoded | null {
  const a = call.a;
  const quad = (): [number, number, number, number] => [a[0] as number, a[1] as number, a[2] as number, a[3] as number];
  switch (call.k) {
    case 'poly': return { quad: null, ring: a[0] as SecPt[], style: a[1] as PolyStyle, color: a[2] as string, alpha: a[3] as number | undefined };
    case 'profile': return { quad: quad(), ring: null, style: a[4] ? 'cut' : 'cut-hatched', color: a[5] as string };
    case 'band': return { quad: quad(), ring: null, style: 'band', color: a[4] as string, alpha: a[5] as number };
    case 'glass': return { quad: quad(), ring: null, style: 'glass', color: (a[4] as string | undefined) ?? '' };
    case 'elev': case 'outline': case 'window': return { quad: quad(), ring: null, style: call.k, color: a[4] as string };
    case 'vedge': case 'line': return null;
    default: { const never: never = call.k; return never; }
  }
}

/** The columns a decoded call's region can span — its own, or wider —, or null
 *  for none. */
function callSpan(d: Decoded | null): [number, number] | null {
  if (!d) return null;
  let a = Infinity, b = -Infinity;
  if (d.quad) { a = d.quad[0]; b = d.quad[1]; } else for (const p of d.ring!) { if (p.t < a) a = p.t; if (p.t > b) b = p.t; }
  return Number.isFinite(a) && Number.isFinite(b) ? [a, b] : null;
}

function polyCall(dec: Decoded, ring: SecPt[]): SecCall {
  const a = dec.alpha == null ? [ring, dec.style, dec.color] : [ring, dec.style, dec.color, dec.alpha];
  return dec.src ? { k: 'poly', a, src: dec.src } : { k: 'poly', a };
}

/** A polyline in t (strictly increasing). */
interface Chain { t: number[]; h: number[] }

/** A t-monotone region over [t0, t1]: between `lo` and `hi`. As the subject of
 *  a clip it carries its own boundary's flags: each chain segment's curve flag,
 *  each chain vertex's sample flag, and its sides' heights (ascending) with
 *  theirs (undefined: no vertex of the call's own). A WINDOW of a region keeps
 *  its chains, which then run past [t0, t1], and a side that is a window's end
 *  (`leftClip` / `rightClip`) is a clip edge, not its own. */
interface Region {
  t0: number; t1: number; h0: number; h1: number;
  lo: Chain; hi: Chain;
  loC: boolean[]; hiC: boolean[];
  loS: Array<boolean | undefined>; hiS: Array<boolean | undefined>;
  left: number[]; leftS: Array<boolean | undefined>;
  right: number[]; rightS: Array<boolean | undefined>;
  leftClip?: boolean; rightClip?: boolean;
  /** An axis-aligned rectangle (a quad call's region, or a window of one). */
  rect?: boolean;
}

/** A rectangle's flags: no curve, no sample (a region is never written). */
const NO_CURVE = [false], NO_SAMPLE = [false, false];

function regionOfDecoded(d: Decoded): Region | null {
  if (!d.quad) return regionOf(d.ring!);
  const [t0, t1, h0, h1] = d.quad;
  if (!(Number.isFinite(t0) && Number.isFinite(t1) && Number.isFinite(h0) && Number.isFinite(h1)) || !(t1 - t0 > EPS_T) || !(h1 > h0)) return null;
  const ts = [t0, t1], side = [h0, h1];
  return {
    t0, t1, h0, h1, lo: { t: ts, h: [h0, h0] }, hi: { t: ts, h: [h1, h1] },
    loC: NO_CURVE, hiC: NO_CURVE, loS: NO_SAMPLE, hiS: NO_SAMPLE,
    left: side, leftS: NO_SAMPLE, right: side, rightS: NO_SAMPLE, rect: true,
  };
}

/** A counter-clockwise t-monotone ring as a region — bottom chain left to right,
 *  its right side up, top chain back, its left side down — or null. */
function regionOf(ring: readonly SecPt[]): Region | null {
  const P: SecPt[] = [];
  for (const p of ring) {
    if (!Number.isFinite(p.t) || !Number.isFinite(p.h)) return null;
    const q = P[P.length - 1];
    if (q && Math.abs(p.t - q.t) <= EPS_T && Math.abs(p.h - q.h) <= EPS_T) { P[P.length - 1] = { ...q, c: p.c, s: q.s && p.s }; continue; }
    P.push(p);
  }
  while (P.length > 1 && Math.abs(P[0].t - P[P.length - 1].t) <= EPS_T && Math.abs(P[0].h - P[P.length - 1].h) <= EPS_T) P.pop();
  const n = P.length;
  if (n < 3) return null;
  let area = 0, t0 = Infinity, t1 = -Infinity, h0 = Infinity, h1 = -Infinity;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    area += P[j].t * P[i].h - P[i].t * P[j].h;
    t0 = Math.min(t0, P[i].t); t1 = Math.max(t1, P[i].t); h0 = Math.min(h0, P[i].h); h1 = Math.max(h1, P[i].h);
  }
  if (!(area > 0) || !(t1 - t0 > EPS_T)) return null;
  const onL = (p: SecPt): boolean => p.t <= t0 + EPS_T, onR = (p: SecPt): boolean => p.t >= t1 - EPS_T;
  let b = -1;
  for (let i = 0; i < n; i++) if (onL(P[i]) && !onL(P[(i + 1) % n])) { if (b >= 0) return null; b = i; }
  if (b < 0) return null;
  const Q = [...P.slice(b), ...P.slice(0, b)];
  let k = 0;
  const lo = [Q[0]];
  while (++k < n && !onR(Q[k])) { if (!(Q[k].t > lo[lo.length - 1].t + EPS_T)) return null; lo.push(Q[k]); }
  if (k >= n) return null;
  lo.push(Q[k]);
  const right = [Q[k]];
  while (k + 1 < n && onR(Q[k + 1])) { k++; if (!(Q[k].h > right[right.length - 1].h)) return null; right.push(Q[k]); }
  const hi = [Q[k]];
  while (++k < n && !onL(Q[k])) { if (!(Q[k].t < hi[hi.length - 1].t - EPS_T)) return null; hi.push(Q[k]); }
  const tl = k < n ? k : 0;
  hi.push(Q[tl]);
  const left = [Q[tl]];
  for (let j = tl + 1; tl > 0 && j <= n; j++) {
    const p = Q[j % n];
    if (!onL(p) || !(p.h < left[left.length - 1].h)) return null;
    left.push(p);
  }
  const H = hi.slice().reverse();
  const chain = (pts: SecPt[]): Chain => {
    const t = pts.map((p) => p.t), h = pts.map((p) => p.h);
    t[0] = t0; t[t.length - 1] = t1;
    return { t, h };
  };
  left.reverse();
  return {
    t0, t1, h0, h1, lo: chain(lo), hi: chain(H),
    loC: lo.slice(0, -1).map((p) => !!p.c),
    hiC: H.slice(1).map((p) => !!p.c),
    loS: lo.map((p) => !!p.s), hiS: H.map((p) => !!p.s),
    left: left.map((p) => p.h), leftS: left.map((p) => !!p.s),
    right: right.map((p) => p.h), rightS: right.map((p) => !!p.s),
  };
}

/** Index of the chain's vertex at t (within EPS_T), or -1. */
function vertexAt(c: Chain, t: number): number {
  const ts = c.t;
  let lo = 0, hi = ts.length - 1;
  while (hi - lo > 1) { const m = (lo + hi) >> 1; if (ts[m] <= t) lo = m; else hi = m; }
  if (Math.abs(ts[lo] - t) <= EPS_T) return lo;
  if (Math.abs(ts[hi] - t) <= EPS_T) return hi;
  return -1;
}

function valueAt(c: Chain, t: number): number {
  const ts = c.t, n = ts.length;
  if (t <= ts[0]) return c.h[0];
  if (t >= ts[n - 1]) return c.h[n - 1];
  let lo = 0, hi = n - 1;
  while (hi - lo > 1) { const m = (lo + hi) >> 1; if (ts[m] <= t) lo = m; else hi = m; }
  if (t - ts[lo] <= EPS_T) return c.h[lo];
  if (ts[hi] - t <= EPS_T) return c.h[hi];
  return c.h[lo] + ((c.h[hi] - c.h[lo]) * (t - ts[lo])) / (ts[hi] - ts[lo]);
}

/** The chain's segment holding t (a column's middle). */
function segmentAt(c: Chain, t: number): number {
  const ts = c.t;
  let lo = 0, hi = ts.length - 1;
  while (hi - lo > 1) { const m = (lo + hi) >> 1; if (ts[m] <= t) lo = m; else hi = m; }
  return lo;
}

const spans = (c: Chain, ta: number, tb: number): boolean => c.t[0] <= ta + EPS_T && c.t[c.t.length - 1] >= tb - EPS_T;

// ---- The cells -------------------------------------------------------------------

/** A region where a face is — its silhouette, or where it paints opaque (`o`) —,
 *  which can set the depth of what it holds. */
interface Zone { r: Region; face: number; o: boolean }
/** What is on the far side of a boundary edge: nothing of the subject (its own
 *  edge), a nearer face (a clip edge) or the subject at another depth (a split). */
type Kind = 0 | 1 | 2;
const OWN = 0, CLIP = 1, SPLIT = 2;
/** A cell of one column: from chain `lo` up to chain `hi`, painted at depth `d`,
 *  in gap `gap` of the column (the stretches between the occluders). */
interface Cell { lo: number; hi: number; d: number; gap: number }
/** The subject cut into cells: its chains (the subject's two, then each
 *  occluder's, then each zone's) and what an edge along each is, the
 *  columns, every chain's height at each column edge and the cells. `whole`:
 *  nothing covers it (one gap per column, the subject's whole height); `same`:
 *  and one depth paints it all. */
interface Grid { S: Region; chains: Chain[]; kinds: Kind[]; T: number[]; V: number[][]; cells: Cell[][]; whole: boolean; same: boolean }

/** The columns: every chain's vertex inside the subject's span, then every
 *  crossing of two chains between them — so inside a column no two cross. */
function columns(S: Region, chains: readonly Chain[]): number[] {
  const ts: number[] = [S.t0, S.t1], own: boolean[] = [true, true];
  chains.forEach((c, i) => {
    for (const t of c.t) if (t > S.t0 - EPS_T && t < S.t1 + EPS_T) { ts.push(Math.max(S.t0, Math.min(S.t1, t))); own.push(i < 2); }
  });
  const merge = (): number[] => {
    const idx = ts.map((_, i) => i).sort((a, b) => ts[a] - ts[b]);
    const out: number[] = [];
    for (let i = 0; i < idx.length;) {
      let j = i + 1;
      while (j < idx.length && ts[idx[j]] - ts[idx[j - 1]] <= EPS_T) j++;
      let rep = idx[i];
      for (let k = i; k < j; k++) if (own[idx[k]]) { rep = idx[k]; break; }
      out.push(ts[rep]);
      i = j;
    }
    return out;
  };
  const T = merge();
  for (let i = 0; i + 1 < T.length; i++) {
    const ta = T[i], tb = T[i + 1];
    const act = chains.filter((c) => spans(c, ta, tb));
    const va = act.map((c) => valueAt(c, ta)), vb = act.map((c) => valueAt(c, tb));
    for (let p = 0; p < act.length; p++) for (let q = p + 1; q < act.length; q++) {
      const d0 = va[p] - va[q], d1 = vb[p] - vb[q];
      if (!((d0 > EPS_V && d1 < -EPS_V) || (d0 < -EPS_V && d1 > EPS_V))) continue;
      const tc = ta + ((tb - ta) * d0) / (d0 - d1);
      if (tc > ta + EPS_T && tc < tb - EPS_T) { ts.push(tc); own.push(false); }
    }
  }
  return merge();
}

/** Every chain's height at each column edge, the heights of one point made one
 *  number (a vertex's own height first, the subject's first of all), so cells
 *  meeting there share it exactly. */
function canonical(T: readonly number[], chains: readonly Chain[]): number[][] {
  return T.map((t) => {
    const items: Array<{ id: number; v: number; exact: boolean }> = [];
    chains.forEach((c, id) => {
      if (!(c.t[0] <= t + EPS_T && c.t[c.t.length - 1] >= t - EPS_T)) return;
      const k = vertexAt(c, t);
      items.push({ id, v: k >= 0 ? c.h[k] : valueAt(c, t), exact: k >= 0 });
    });
    items.sort((a, b) => a.v - b.v);
    const row = new Array<number>(chains.length).fill(NaN);
    for (let i = 0; i < items.length;) {
      let j = i + 1;
      while (j < items.length && items[j].v - items[j - 1].v <= EPS_V) j++;
      const g = items.slice(i, j);
      const rep = g.find((x) => x.exact && x.id < 2) ?? g.find((x) => x.exact) ?? g.find((x) => x.id < 2) ?? g[0];
      for (const x of g) row[x.id] = rep.v;
      i = j;
    }
    return row;
  });
}

/** Intervals `a` minus intervals `b`, each joined where they touch first. */
function minus(a: ReadonlyArray<readonly [number, number]>, b: ReadonlyArray<readonly [number, number]>): Array<[number, number]> {
  const A = joined(a), B = joined(b), out: Array<[number, number]> = [];
  for (let i = 0; i < A.length; i++) {
    const a1 = A[i][1];
    let cur = A[i][0];
    for (let j = 0; j < B.length; j++) {
      const b0 = B[j][0], b1 = B[j][1];
      if (b1 <= cur || b0 >= a1) continue;
      if (b0 > cur) out.push([cur, b0]);
      if (b1 > cur) cur = b1;
      if (cur >= a1) break;
    }
    if (cur < a1) out.push([cur, a1]);
  }
  return out;
}

/** Intervals sorted and joined where they touch, the empty ones dropped. */
function joined(iv: ReadonlyArray<readonly [number, number]>): Array<[number, number]> {
  let sorted = iv;
  for (let k = 1; k < iv.length; k++) if (iv[k][0] < iv[k - 1][0]) { sorted = iv.slice().sort((x, y) => x[0] - y[0]); break; }
  const out: Array<[number, number]> = [];
  for (let k = 0; k < sorted.length; k++) {
    const l = sorted[k][0], h = sorted[k][1];
    if (!(h > l)) continue;
    const last = out.length ? out[out.length - 1] : null;
    if (last && l <= last[1]) { if (h > last[1]) last[1] = h; } else out.push([l, h]);
  }
  return out;
}

/** The subject `S` minus the occluders, cut into cells, each at the depth
 *  `depthOf` gives for the zones holding its middle (each as its face twice
 *  over, plus 1 where it paints opaque). */
function cells(S: Region, occ: readonly Region[], zones: readonly Zone[], depthOf: (tm: number, faces: readonly number[]) => Depth): Grid {
  const chains: Chain[] = [S.lo, S.hi];
  const kinds: Kind[] = [OWN, OWN];
  for (const o of occ) { chains.push(o.lo, o.hi); kinds.push(CLIP, CLIP); }
  for (const z of zones) { chains.push(z.r.lo, z.r.hi); kinds.push(SPLIT, SPLIT); }
  const zBase = 2 + 2 * occ.length;
  const T = columns(S, chains);
  const V = canonical(T, chains);
  const grid: Cell[][] = [];
  let whole = true, sameD: number | null = null, oneD = true;
  for (let i = 0; i + 1 < T.length; i++) {
    const ta = T[i], tb = T[i + 1], tm = (ta + tb) / 2;
    const row: Cell[] = [];
    grid.push(row);
    const sLo = valueAt(S.lo, tm), sHi = valueAt(S.hi, tm);
    if (!(sHi - sLo > THIN)) continue;
    const ivs: Array<{ a: number; b: number; lo: number; hi: number }> = [];
    occ.forEach((o, k) => {
      if (!spans(o.lo, ta, tb)) return;
      const a = Math.max(valueAt(o.lo, tm), sLo), b = Math.min(valueAt(o.hi, tm), sHi);
      if (b - a > THIN) ivs.push({ a, b, lo: 2 + 2 * k, hi: 3 + 2 * k });
    });
    ivs.sort((x, y) => x.a - y.a);
    const gaps: Array<{ a: number; b: number; lo: number; hi: number }> = [];
    let cur = sLo, curId = 0;
    for (const iv of ivs) {
      if (iv.a > cur + THIN) gaps.push({ a: cur, b: iv.a, lo: curId, hi: iv.lo });
      if (iv.b > cur) { cur = iv.b; curId = iv.hi; }
    }
    if (sHi > cur + THIN) gaps.push({ a: cur, b: sHi, lo: curId, hi: 1 });
    const zv: Array<{ a: number; b: number; lo: number; hi: number; key: number }> = [];
    zones.forEach((z, k) => { if (spans(z.r.lo, ta, tb)) zv.push({ a: valueAt(z.r.lo, tm), b: valueAt(z.r.hi, tm), lo: zBase + 2 * k, hi: zBase + 2 * k + 1, key: 2 * z.face + (z.o ? 1 : 0) }); });
    gaps.forEach((g, gi) => {
      const cuts = [{ v: g.a, id: g.lo }];
      for (const z of zv) for (const [v, id] of [[z.a, z.lo], [z.b, z.hi]]) if (v > g.a + THIN && v < g.b - THIN) cuts.push({ v, id });
      cuts.sort((x, y) => x.v - y.v);
      cuts.push({ v: g.b, id: g.hi });
      let last: Cell | null = null;
      for (let k = 0; k + 1 < cuts.length; k++) {
        if (!(cuts[k + 1].v - cuts[k].v > THIN)) continue;
        const m = (cuts[k].v + cuts[k + 1].v) / 2;
        const at: number[] = [];
        for (const z of zv) if (z.a <= m && m <= z.b && !at.includes(z.key)) at.push(z.key);
        const d = valueOf(depthOf(tm, at));
        if (last && last.d === d) last.hi = cuts[k + 1].id;
        else row.push(last = { lo: cuts[k].id, hi: cuts[k + 1].id, d, gap: gi });
      }
    });
    if (gaps.length !== 1 || gaps[0].lo !== 0 || gaps[0].hi !== 1 || !row.length || row[0].lo !== 0 || row[row.length - 1].hi !== 1) whole = false;
    for (const c of row) { if (sameD == null) sameD = c.d; else if (c.d !== sameD) oneD = false; }
  }
  return { S, chains, kinds, T, V, cells: grid, whole, same: whole && oneD };
}

/** The cells of a window of a call, as what it shows of them: the depths, in
 *  the order its columns meet them, its pieces at each depth and its whole
 *  region (each gap's cells as one). */
interface Part { depths: number[]; at: (d: number) => SecPt[][]; region: () => SecPt[][] }

function gridPart(G: Grid): Part {
  const depths: number[] = [];
  for (const row of G.cells) for (const c of row) if (!depths.includes(c.d)) depths.push(c.d);
  return {
    depths,
    at: (d) => (depths.length === 1 ? rings(G, G.cells, null)
      : rings(G, G.cells.map((row) => row.filter((c) => c.d === d)), G.cells.map((row) => row.filter((c) => c.d !== d)))),
    region: () => rings(G, G.cells.map((row) => {
      const u: Array<{ lo: number; hi: number }> = [];
      row.forEach((c, k) => { if (k > 0 && row[k - 1].gap === c.gap) u[u.length - 1].hi = c.hi; else u.push({ lo: c.lo, hi: c.hi }); });
      return u;
    }), null),
  };
}

/** A window cut into bands (`bandsCut`), all at one depth. */
const bandsPart = (pieces: SecPt[][], d: number): Part => ({ depths: pieces.length ? [d] : [], at: () => pieces, region: () => pieces });

/** What a call shows of its windows' parts, in column order, as one grid of
 *  them all would: at one depth its pieces; at several, when OPAQUE its whole
 *  region at the farthest and a mask over each part painted nearer, else its
 *  pieces depth by depth. `own`: the call's own grid — nothing covering it,
 *  the call itself stands for its region. */
function shownOf(call: SecCall, dec: Decoded, parts: readonly Part[], own: Grid | null): SecShown[] {
  const depths: number[] = [];
  for (const p of parts) for (const d of p.depths) if (!depths.includes(d)) depths.push(d);
  if (!depths.length) return [];
  const out: SecShown[] = [];
  const each = (d: number, mask: boolean): void => {
    for (const p of parts) if (p.depths.includes(d)) for (const r of p.at(d)) out.push({ d, call: polyCall(dec, mask ? maskOf(r) : r) });
  };
  if (depths.length === 1) {
    if (own?.same) return [{ d: depths[0], call }];
    each(depths[0], false);
    return out;
  }
  if (!paintsOpaque(dec)) { for (const d of depths) each(d, false); return out; }
  const far = Math.max(...depths);
  if (own?.whole) out.push({ d: far, call });
  else for (const p of parts) for (const r of p.region()) out.push({ d: far, call: polyCall(dec, r) });
  for (const d of depths) if (d !== far) each(d, true);
  return out;
}

// ---- The boundary ------------------------------------------------------------------

/** A point of the grid: column edge `e`, height `h`. */
interface Pt { t: number; h: number; e: number }
/** A boundary edge: `src` its chain, or −1 a clip edge at a column edge, −4 a
 *  split there, −2 / −3 the subject's left / right side; `seg` the chain's
 *  segment (a side's piece between its own heights); `ka` / `kb` what is on its
 *  far side at its start / its end (one kind, but for a run of non-own edges in
 *  line joined into one). */
interface Edge { a: Pt; b: Pt; src: number; seg: number; ka: Kind; kb: Kind }

/** The rings of a set of cells (`rows`, per column; `others`: the subject's
 *  cells at other depths, which make a boundary a split), each hole joined to
 *  its outline. Where a hole finds no outline (which should not be) the cells
 *  themselves are the pieces. */
function rings(G: Grid, rows: ReadonlyArray<ReadonlyArray<{ lo: number; hi: number }>>, others: ReadonlyArray<ReadonlyArray<{ lo: number; hi: number }>> | null): SecPt[][] {
  const { S, chains, kinds, T, V } = G;
  const last = T.length - 1;
  const pts = T.map(() => new Map<number, Pt>());
  const P = (e: number, v: number): Pt => {
    let p = pts[e].get(v);
    if (!p) pts[e].set(v, (p = { t: T[e], h: v, e }));
    return p;
  };
  const E: Edge[] = [];
  rows.forEach((row, i) => {
    const tm = (T[i] + T[i + 1]) / 2;
    for (const c of row) {
      E.push({ a: P(i, V[i][c.lo]), b: P(i + 1, V[i + 1][c.lo]), src: c.lo, seg: segmentAt(chains[c.lo], tm), ka: kinds[c.lo], kb: kinds[c.lo] });
      E.push({ a: P(i + 1, V[i + 1][c.hi]), b: P(i, V[i][c.hi]), src: c.hi, seg: segmentAt(chains[c.hi], tm), ka: kinds[c.hi], kb: kinds[c.hi] });
    }
  });
  // Where a column edge is the side of a cell on one side only: its own side,
  // a clip edge (nothing of the subject beyond: a nearer face) or a split (the
  // subject beyond at another depth), cut at every height a cell meets it, so a
  // point where the region touches itself is a vertex of each loop through it.
  const at = (row: ReadonlyArray<{ lo: number; hi: number }> | undefined, e: number): Array<[number, number]> => (row ?? []).map((c) => [V[e][c.lo], V[e][c.hi]]);
  const inside = (iv: Array<[number, number]>, y: number): boolean => iv.some(([a, b]) => a <= y && y <= b);
  for (let e = 0; e <= last; e++) {
    const before = at(rows[e - 1], e), after = at(rows[e], e);
    if (!before.length && !after.length) continue;
    const own = e === 0 && !S.leftClip ? S.left : e === last && !S.rightClip ? S.right : null;
    const meet: number[] = [];
    for (const row of [G.cells[e - 1], G.cells[e]]) for (const c of row ?? []) meet.push(V[e][c.lo], V[e][c.hi]);
    const add = (y0: number, y1: number, up: boolean, beyond: Array<[number, number]>): void => {
      const ys = [...new Set([y0, y1, ...meet.filter((y) => y > y0 && y < y1), ...(own ?? []).filter((y) => y > y0 && y < y1)])].sort((a, b) => a - b);
      const run: Edge[] = [];
      for (let k = 0; k + 1 < ys.length; k++) {
        const mid = (ys[k] + ys[k + 1]) / 2;
        const kind: Kind = own ? OWN : inside(beyond, mid) ? SPLIT : CLIP;
        const src = own ? (e === 0 ? -2 : -3) : kind === SPLIT ? -4 : -1;
        run.push({ a: P(e, ys[k]), b: P(e, ys[k + 1]), src, seg: own ? own.filter((y) => y < mid).length : 0, ka: kind, kb: kind });
      }
      if (up) E.push(...run);
      else E.push(...run.reverse().map((r) => ({ ...r, a: r.b, b: r.a })));
    };
    for (const [y0, y1] of minus(before, after)) add(y0, y1, true, others ? at(others[e], e) : []);
    for (const [y0, y1] of minus(after, before)) add(y0, y1, false, others ? at(others[e - 1], e) : []);
  }
  const out = ringsOf(G, E);
  return out ?? cellRings(G, rows);
}

const isOwn = (k: Kind): boolean => k === OWN;

/** The subject's own flag for a grid point that is one of its vertices: its
 *  sample flag, or undefined for a point that is not. */
function origFlag(G: Grid, p: Pt): boolean | undefined {
  const { S } = G, last = G.T.length - 1;
  if (p.e === 0 || p.e === last) {
    const side = p.e === 0 ? S.left : S.right, k = side.indexOf(p.h);
    if (k >= 0) return (p.e === 0 ? S.leftS : S.rightS)[k];
  }
  const m = vertexAt(S.hi, p.t);
  if (m >= 0 && S.hi.h[m] === p.h) return S.hiS[m];
  const k = vertexAt(S.lo, p.t);
  if (k >= 0 && S.lo.h[k] === p.h) return S.loS[k];
  return undefined;
}

/** The loops of a set of boundary edges, each a ring of the subject's points with
 *  its flags: an edge of the subject's own keeps its curve flag, every other one
 *  is a clip edge (`x`); a new point on a curve chord is a sample, and so is one
 *  between two clip edges (a nearer face's corner, which that face offers) or on
 *  a straight edge of its own where a split leaves it (the edge runs on, at
 *  another depth). A hole is joined to its outline by a bridge walked both ways,
 *  so every piece is ONE ring and fills without a seam. Slivers are dropped.
 *  Null when a hole lies in no outline. */
function ringsOf(G: Grid, E: Edge[]): SecPt[][] | null {
  const { S } = G;
  const from = new Map<Pt, Edge[]>();
  for (const e of E) { const l = from.get(e.a); if (l) l.push(e); else from.set(e.a, [e]); }
  const used = new Set<Edge>();
  const loops: Edge[][] = [];
  const TAU = 2 * Math.PI;
  for (const e0 of E) {
    if (used.has(e0)) continue;
    used.add(e0);
    const loop = [e0];
    let cur = e0, closed = false;
    for (;;) {
      // The first edge clockwise from the way back: each loop keeps to one
      // side of a point where the region touches itself.
      const back = Math.atan2(cur.a.h - cur.b.h, cur.a.t - cur.b.t);
      let best: Edge | null = null, bestTurn = Infinity;
      for (const e of from.get(cur.b) ?? []) {
        if (used.has(e) && e !== e0) continue;
        let turn = (back - Math.atan2(e.b.h - e.a.h, e.b.t - e.a.t)) % TAU;
        if (turn <= 1e-12) turn += TAU;
        if (turn < bestTurn) { bestTurn = turn; best = e; }
      }
      if (!best) break;
      if (best === e0) { closed = true; break; }
      used.add(best); loop.push(best); cur = best;
    }
    if (closed) loops.push(loop);
  }
  const pinch = (p: Pt): boolean => (from.get(p)?.length ?? 0) > 1;
  const curve = (e: Edge): boolean => isOwn(e.ka) && (e.src === 0 ? S.loC[e.seg] : e.src === 1 ? S.hiC[e.seg] : false);
  const rs = loops.map((l) => simplify(l, pinch)).filter((l) => l.length >= 3).map((l) => {
    const n = l.length;
    return tidy(l.map((e, k): SecPt => {
      const prev = l[(k + n - 1) % n];
      const orig = origFlag(G, e.a);
      const s = orig ?? (curve(prev) || curve(e) || (!isOwn(prev.kb) && !isOwn(e.ka)) || prev.kb === SPLIT || e.ka === SPLIT);
      return { t: e.a.t, h: e.a.h, ...(s ? { s: true } : {}), ...(isOwn(e.ka) ? (curve(e) ? { c: true } : {}) : { x: true }) };
    }));
  }).filter((r) => r.length >= 3).filter(notSliver);
  const outers = rs.filter((r) => signedArea(r) > 0), holes = rs.filter((r) => signedArea(r) < 0);
  const own = outers.map(() => [] as SecPt[][]);
  for (const h of holes) {
    // Its outline: the smallest one larger than it holding a point just
    // OUTSIDE it — a point inside could lie in an island the hole holds, and
    // its ring's first point on an outline it touches.
    const q = outsidePoint(h), a = -signedArea(h);
    let best = -1;
    outers.forEach((o, i) => { const ao = signedArea(o); if (ao > a && insideRing(q, o) && (best < 0 || ao < signedArea(outers[best]))) best = i; });
    if (best < 0) return null;
    own[best].push(h);
  }
  return outers.flatMap((o, i) => (own[i].length ? joinHoles(o, own[i]) : [o]));
}

function signedArea(r: readonly Pick<SecPt, 't' | 'h'>[]): number {
  let a = 0;
  for (let i = 0, j = r.length - 1; i < r.length; j = i++) a += r[j].t * r[i].h - r[i].t * r[j].h;
  return a / 2;
}

function notSliver(r: readonly SecPt[]): boolean {
  let a = 0, len = 0;
  for (let i = 0, j = r.length - 1; i < r.length; j = i++) { a += r[j].t * r[i].h - r[i].t * r[j].h; len += Math.hypot(r[i].t - r[j].t, r[i].h - r[j].h); }
  return Math.abs(a / 2) > 1e-6 && Math.abs(a) / len >= SLIVER;
}

function insideRing(p: Pick<SecPt, 't' | 'h'>, r: readonly SecPt[]): boolean {
  let c = false;
  for (let i = 0, j = r.length - 1; i < r.length; j = i++) if ((r[i].h > p.h) !== (r[j].h > p.h) && p.t < ((r[j].t - r[i].t) * (p.h - r[i].h)) / (r[j].h - r[i].h) + r[i].t) c = !c;
  return c;
}

/** A point just outside a hole (a clockwise ring): off the middle of its
 *  longest edge, on its left. */
function outsidePoint(r: readonly SecPt[]): { t: number; h: number } {
  let best = 0, len = -1;
  r.forEach((p, i) => { const q = r[(i + 1) % r.length], l = Math.hypot(q.t - p.t, q.h - p.h); if (l > len) { len = l; best = i; } });
  const p = r[best], q = r[(best + 1) % r.length], k = 1e-6 / len;
  return { t: (p.t + q.t) / 2 - (q.h - p.h) * k, h: (p.h + q.h) / 2 + (q.t - p.t) * k };
}

/** The cells themselves as pieces, joined by clip edges: the boundary's
 *  fallback, never needed while every hole lies in its outline. */
function cellRings(G: Grid, rows: ReadonlyArray<ReadonlyArray<{ lo: number; hi: number }>>): SecPt[][] {
  const { S, kinds, T, V } = G, last = T.length - 1;
  const out: SecPt[][] = [];
  rows.forEach((row, i) => {
    const tm = (T[i] + T[i + 1]) / 2;
    for (const c of row) {
      // Its corners counter-clockwise, each with the edge it starts: the bottom,
      // the right side, the top, the left side — own only on the subject's boundary.
      const corner = (e: number, h: number, own: boolean, curve: boolean): SecPt => {
        const s = origFlag(G, { t: T[e], h, e }) ?? true;
        return { t: T[e], h, ...(s ? { s: true } : {}), ...(own ? (curve ? { c: true } : {}) : { x: true }) };
      };
      const r = tidy([
        corner(i, V[i][c.lo], kinds[c.lo] === OWN, c.lo === 0 && S.loC[segmentAt(S.lo, tm)]),
        corner(i + 1, V[i + 1][c.lo], i + 1 === last && !S.rightClip, false),
        corner(i + 1, V[i + 1][c.hi], kinds[c.hi] === OWN, c.hi === 1 && S.hiC[segmentAt(S.hi, tm)]),
        corner(i, V[i][c.hi], i === 0 && !S.leftClip, false),
      ]);
      if (r.length >= 3 && notSliver(r)) out.push(r);
    }
  });
  return out;
}

/** A ring with points closer than a micron made one — `cleanPts`' rule: a vertex
 *  if either was, the edge onward the later one's. */
function tidy(r: SecPt[]): SecPt[] {
  const out: SecPt[] = [];
  const close = (p: SecPt, q: SecPt): boolean => Math.hypot(p.t - q.t, p.h - q.h) < 1e-6;
  const one = (q: SecPt, p: SecPt): SecPt => ({ t: q.t, h: q.h, ...(q.s && p.s ? { s: true } : {}), ...(p.c ? { c: true } : {}), ...(p.x ? { x: true } : {}) });
  for (const p of r) {
    const q = out[out.length - 1];
    if (q && close(p, q)) out[out.length - 1] = one(q, p); else out.push(p);
  }
  while (out.length > 1 && close(out[0], out[out.length - 1])) {
    const l = out.pop()!;
    if (!l.s && out[0].s) { const { s: _s, ...vertex } = out[0]; out[0] = vertex; }
  }
  return out;
}

/** Merges a loop's consecutive edges along one segment of one chain (the
 *  column edges between them are no vertex), consecutive pieces of one side
 *  between its own heights, and consecutive non-own edges in line — never
 *  through a point where the region touches itself (`pinch`). */
function simplify(loop: Edge[], pinch: (p: Pt) => boolean): Edge[] {
  const n = loop.length;
  const joins = (p: Edge, q: Edge): boolean => {
    if (pinch(p.b)) return false;
    if (p.src === q.src && p.seg === q.seg) {
      if (p.src >= 0) return true;
      return p.a.t === p.b.t && q.a.t === q.b.t && (p.b.h > p.a.h) === (q.b.h > q.a.h);
    }
    if (isOwn(p.kb) || isOwn(q.ka)) return false;
    const ux = p.b.t - p.a.t, uy = p.b.h - p.a.h, vx = q.b.t - q.a.t, vy = q.b.h - q.a.h;
    return Math.abs(ux * vy - uy * vx) <= 1e-12 * Math.hypot(ux, uy) * Math.hypot(vx, vy) && ux * vx + uy * vy > 0;
  };
  let start = 0;
  while (start < n && joins(loop[(start + n - 1) % n], loop[start])) start++;
  if (start === n) return [];
  const out: Edge[] = [];
  for (let k = 0; k < n; k++) {
    const e = loop[(start + k) % n];
    const prev = out[out.length - 1];
    if (prev && joins(prev, e)) out[out.length - 1] = { ...prev, b: e.b, kb: e.kb };
    else out.push(e);
  }
  return out;
}

/** An outline and the holes it holds as one ring: each hole bridged to it, the
 *  bridge walked both ways and flagged `x` (its two edges cancel in the fill).
 *  Where a hole cannot be bridged, convex pieces instead, joined by `x` edges. */
function joinHoles(outer: SecPt[], holes: SecPt[][]): SecPt[][] {
  type W = { x: number; y: number; p: SecPt };
  const wrap = (r: SecPt[]): W[] => r.map((p) => ({ x: p.t, y: p.h, p }));
  const ow = wrap(outer), hw = holes.map(wrap);
  const ring = bridgeHoles(ow, hw) as W[];
  if (ring.length === ow.length + hw.reduce((n, h) => n + h.length + 2, 0)) {
    const edges = new Set<string>();
    const id = new Map<W, number>();
    ring.forEach((w) => { if (!id.has(w)) id.set(w, id.size); });
    ring.forEach((w, i) => edges.add(`${id.get(w)}>${id.get(ring[(i + 1) % ring.length])}`));
    return [ring.map((w, i) => {
      const next = ring[(i + 1) % ring.length];
      return edges.has(`${id.get(next)}>${id.get(w)}`) ? { t: w.p.t, h: w.p.h, ...(w.p.s ? { s: true } : {}), x: true } : { ...w.p };
    })];
  }
  // A fallback that should not be needed: convex pieces of the triangulation.
  const tris = earClip(ow, hw);
  const flags = new Map<string, SecPt>();
  for (const r of [outer, ...holes]) r.forEach((p, i) => flags.set(`${p.t},${p.h}>${r[(i + 1) % r.length].t},${r[(i + 1) % r.length].h}`, p));
  return convexMerge(tris).map((piece) => piece.map((v, i) => {
    const w = v as W, q = piece[(i + 1) % piece.length] as W;
    const f = flags.get(`${w.p.t},${w.p.h}>${q.p.t},${q.p.h}`);
    return f ? { ...f } : { t: w.p.t, h: w.p.h, ...(w.p.s ? { s: true } : {}), x: true };
  }));
}
